/*!
 * SPDX-FileCopyrightText: 2024 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { showInfo, showWarning } from '@nextcloud/dialogs';
import { emit } from '@nextcloud/event-bus';
import { defaultRemoteURL, defaultRootPath, getClient, getDefaultPropfind, resultToNode } from '@nextcloud/files/dav';
import { t } from '@nextcloud/l10n';
import { join } from '@nextcloud/paths';
import { openConflictPicker } from '@nextcloud/upload';
import { logger } from '../utils/logger.ts';
/**
 * This represents a Directory in the file tree
 * We extend the File class to better handling uploading
 * and stay as close as possible as the Filesystem API.
 * This also allow us to hijack the size or lastModified
 * properties to compute them dynamically.
 */
export class Directory extends File {
    _contents;
    constructor(name, contents = []) {
        super([], name, { type: 'httpd/unix-directory' });
        this._contents = contents;
    }
    set contents(contents) {
        this._contents = contents;
    }
    get contents() {
        return this._contents;
    }
    get size() {
        return this._computeDirectorySize(this);
    }
    get lastModified() {
        if (this._contents.length === 0) {
            return Date.now();
        }
        return this._computeDirectoryMtime(this);
    }
    /**
     * Get the last modification time of a file tree
     * This is not perfect, but will get us a pretty good approximation
     *
     * @param directory the directory to traverse
     */
    _computeDirectoryMtime(directory) {
        return directory.contents.reduce((acc, file) => {
            return file.lastModified > acc
                // If the file is a directory, the lastModified will
                // also return the results of its _computeDirectoryMtime method
                // Fancy recursion, huh?
                ? file.lastModified
                : acc;
        }, 0);
    }
    /**
     * Get the size of a file tree
     *
     * @param directory the directory to traverse
     */
    _computeDirectorySize(directory) {
        return directory.contents.reduce((acc, entry) => {
            // If the file is a directory, the size will
            // also return the results of its _computeDirectorySize method
            // Fancy recursion, huh?
            return acc + entry.size;
        }, 0);
    }
}
/**
 * Traverse a file tree using the Filesystem API
 *
 * @param entry the entry to traverse
 */
export async function traverseTree(entry) {
    // Handle file
    if (entry.isFile) {
        return new Promise((resolve, reject) => {
            entry.file(resolve, reject);
        });
    }
    // Handle directory
    logger.debug('Handling recursive file tree', { entry: entry.name });
    const directory = entry;
    const entries = await readDirectory(directory);
    const contents = (await Promise.all(entries.map(traverseTree))).flat();
    return new Directory(directory.name, contents);
}
/**
 * Read a directory using Filesystem API
 *
 * @param directory the directory to read
 */
function readDirectory(directory) {
    const dirReader = directory.createReader();
    return new Promise((resolve, reject) => {
        const entries = [];
        const getEntries = () => {
            dirReader.readEntries((results) => {
                if (results.length) {
                    entries.push(...results);
                    getEntries();
                }
                else {
                    resolve(entries);
                }
            }, (error) => {
                reject(error);
            });
        };
        getEntries();
    });
}
/**
 * @param path - The path relative to the destination root
 * @param destination - The destination folder. When provided, directories are created relative
 *   to its source URL instead of the default user root. This is needed for uploads into
 *   non-default locations like team folders.
 */
export async function createDirectoryIfNotExists(path, destination) {
    const davUrl = destination?.source ?? join(defaultRemoteURL, defaultRootPath);
    const davRoot = destination?.root ?? defaultRootPath;
    const davClient = getClient(davUrl);
    const dirExists = await davClient.exists(path);
    if (!dirExists) {
        logger.debug('Directory does not exist, creating it', { path, davUrl });
        await davClient.createDirectory(path, { recursive: true });
        const stat = await davClient.stat(path, { details: true, data: getDefaultPropfind() });
        emit('files:node:created', resultToNode(stat.data, davRoot, davUrl));
    }
}
/**
 * Resolve conflicts between existing files and incoming files
 *
 * @param files - incoming files
 * @param destination - destination folder
 * @param contents - existing contents of the destination folder
 */
export async function resolveConflict(files, destination, contents) {
    try {
        // List all conflicting files
        const conflicts = files.filter((file) => {
            return contents.find((node) => node.basename === (file instanceof File ? file.name : file.basename));
        }).filter(Boolean);
        // List of incoming files that are NOT in conflict
        const uploads = files.filter((file) => {
            return !conflicts.includes(file);
        });
        // Let the user choose what to do with the conflicting files
        const { selected, renamed } = await openConflictPicker(destination.path, conflicts, contents);
        logger.debug('Conflict resolution', { uploads, selected, renamed });
        // If the user selected nothing, we cancel the upload
        if (selected.length === 0 && renamed.length === 0 && uploads.length === 0) {
            // User skipped
            showInfo(t('files', 'Conflicts resolution skipped'));
            logger.info('User skipped the conflict resolution');
            return [];
        }
        // Update the list of files to upload
        return [...uploads, ...selected, ...renamed];
    }
    catch (error) {
        // User cancelled
        logger.warn('User cancelled the upload', { error });
        showWarning(t('files', 'Upload cancelled'));
    }
    return [];
}
